import { useState } from "react";
import {
  Paper,
  Group,
  Text,
  Select,
  Button,
  Textarea,
  Badge,
  Stack,
  Divider,
  Tooltip,
} from "@mantine/core";
import { IconShieldCheck, IconWorld, IconUsers, IconBan } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import { Endpoints } from "../../lib/endpoints";
import { AdminPermissions } from "../../lib/data";
import PermissionGuard from "../PermissionGuard";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("t")}` },
});

/** Split a free-text list of user IDs (comma / space / newline separated). */
const parseIds = (raw: string): string[] =>
  raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

type Kind = "mission" | "campaign";

interface Props {
  kind: Kind;
  /** Mission or campaign id. */
  id: string;
  /** Current accessTier value from the record (defaults to inherit/all). */
  currentTier?: string | null;
  /** Current allowlist (cohort) user ids. */
  currentAllowUsers?: string[];
  /** Current block list user ids (missions only). */
  currentBlockUsers?: string[];
}

/**
 * Reusable access & tier control block for an opportunity or a campaign.
 *
 * NOTE: gating only takes effect when the backend TIER_ACCESS_MODE=enforce.
 * While it's off/shadow these writes just record intent — safe to set early.
 */
export default function AccessTierControl({
  kind,
  id,
  currentTier,
  currentAllowUsers = [],
  currentBlockUsers = [],
}: Props) {
  const isMission = kind === "mission";

  const tierOptions = isMission
    ? [
        { value: "inherit", label: "Inherit — follow the campaign's setting" },
        { value: "all", label: "Everyone — open to all members" },
        { value: "verified", label: "Verified only — gated to verified members" },
      ]
    : [
        { value: "all", label: "Everyone — open to all members" },
        { value: "verified", label: "Verified only — gated to verified members" },
      ];

  const [tier, setTier] = useState<string>(
    currentTier || (isMission ? "inherit" : "all"),
  );
  const [allow, setAllow] = useState<string>(currentAllowUsers.join("\n"));
  const [block, setBlock] = useState<string>(currentBlockUsers.join("\n"));
  const [busy, setBusy] = useState<string | null>(null);

  const err = (e: any) =>
    notifications.show({
      title: "Error",
      message: e?.response?.data?.message || e?.message || "Request failed",
      color: "red",
    });
  const ok = (message: string) =>
    notifications.show({ title: "Saved", message, color: "green" });

  const saveTier = async () => {
    setBusy("tier");
    try {
      const ep = isMission
        ? Endpoints.SetMissionAccess(id)
        : Endpoints.SetCampaignAccess(id);
      await axios.patch(ep.route, { accessTier: tier }, authHeaders());
      ok(`Access tier set to "${tier}"`);
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };

  const openToAll = async () => {
    setBusy("open");
    try {
      const ep = isMission
        ? Endpoints.OpenMissionToAll(id)
        : Endpoints.OpenCampaignToAll(id);
      await axios.post(ep.route, {}, authHeaders());
      setTier("all");
      ok("Opened to everyone");
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };

  const saveAllow = async () => {
    setBusy("allow");
    try {
      const users = parseIds(allow);
      const ep = isMission
        ? Endpoints.SetMissionCohort(id)
        : Endpoints.SetCampaignCohort(id);
      await axios.put(ep.route, { users }, authHeaders());
      ok(
        users.length
          ? `Allowlist set — only these ${users.length} member(s) can see it`
          : "Allowlist cleared",
      );
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };

  const saveBlock = async () => {
    setBusy("block");
    try {
      const users = parseIds(block);
      const ep = Endpoints.SetMissionBlock(id);
      await axios.put(ep.route, { users }, authHeaders());
      ok(users.length ? `Blocked ${users.length} member(s)` : "Block list cleared");
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };

  if (!id) return null;

  return (
    <PermissionGuard permission={AdminPermissions.MANAGE_VERIFICATION}>
    <Paper withBorder radius="md" p="lg" className="card-glass">
      <Group gap="xs" mb="xs">
        <IconShieldCheck size={18} />
        <Text fw={700}>Access &amp; Tier</Text>
        <Badge color={tier === "verified" ? "green" : tier === "all" ? "blue" : "gray"} variant="light">
          {tier}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed" mb="md">
        Controls who can see and do this {kind}. Gating is live only when
        verification enforcement is switched on; setting it early is safe.
      </Text>

      <Stack gap="sm">
        <Group align="flex-end" gap="sm" wrap="nowrap">
          <Select
            label="Who can access"
            data={tierOptions}
            value={tier}
            onChange={(v) => v && setTier(v)}
            allowDeselect={false}
            style={{ flex: 1 }}
          />
          <Button
            onClick={saveTier}
            loading={busy === "tier"}
            leftSection={<IconShieldCheck size={16} />}
          >
            Save tier
          </Button>
          <Tooltip label="Shortcut: set tier to Everyone">
            <Button
              variant="light"
              onClick={openToAll}
              loading={busy === "open"}
              leftSection={<IconWorld size={16} />}
            >
              Open to all
            </Button>
          </Tooltip>
        </Group>

        <Divider
          my="xs"
          label={
            <Group gap={6}>
              <IconUsers size={14} />
              <Text size="xs">Allowlist (cohort lock)</Text>
            </Group>
          }
        />
        <Textarea
          placeholder="Paste member IDs — one per line, or comma-separated. Leave empty for no allowlist."
          value={allow}
          onChange={(e) => setAllow(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
        />
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            When set, ONLY these members see this {kind} (overrides the tier).
          </Text>
          <Button
            size="xs"
            variant="light"
            onClick={saveAllow}
            loading={busy === "allow"}
          >
            Save allowlist
          </Button>
        </Group>

        {isMission && (
          <>
            <Divider
              my="xs"
              label={
                <Group gap={6}>
                  <IconBan size={14} />
                  <Text size="xs">Block list</Text>
                </Group>
              }
            />
            <Textarea
              placeholder="Paste member IDs to block from this opportunity — one per line."
              value={block}
              onChange={(e) => setBlock(e.currentTarget.value)}
              autosize
              minRows={2}
              maxRows={6}
            />
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                These members can never see this opportunity.
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={saveBlock}
                loading={busy === "block"}
              >
                Save block list
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Paper>
    </PermissionGuard>
  );
}
